// The navigation, as data. A new area adds one line to NAV, with its `group`, and its route in src/routes.
import type { Role } from "@imagora/contracts";
import { ROLES } from "@imagora/contracts";
import { Activity, BarChart3, BellRing, Binoculars, BookCheck, Building2, CalendarCheck, CalendarDays, CalendarRange, CheckSquare, ContactRound, CreditCard, Database, FileBarChart, FileText, Gauge, Gift, Group, HardDriveDownload, ImageIcon, LayoutTemplate, Lightbulb, Link2, ListOrdered, Megaphone, MessageSquareText, Newspaper, Palette, PenLine, Radio, Route, Send, Settings, Shapes, ShieldCheck, SlidersHorizontal, Sparkles, Sun, Target, type LucideIcon, UsersRound, Wand2 } from "lucide-react";
import type { TextKey } from "./i18n/keys";

/** The word on the heading, in the order the sidebar and the More sheet show them. */
export const NAV_GROUPS = [
  { id: "today", label: "nav.group.today" },
  { id: "content", label: "nav.group.content" },
  { id: "ads", label: "nav.group.ads" },
  { id: "customers", label: "nav.group.customers" },
  { id: "results", label: "nav.group.results" },
  { id: "setup", label: "nav.group.setup" },
] as const satisfies ReadonlyArray<{ id: string; label: TextKey }>;

export type NavGroup = (typeof NAV_GROUPS)[number];
export type NavGroupId = NavGroup["id"];

/** Always open and without a heading: its first entry already says the same word. */
export const TOP_GROUP: NavGroupId = "today";

export interface NavItem {
  id: string;
  to: string;
  label: TextKey;
  icon: LucideIcon;
  group: NavGroupId;
  /** Other words a person would type to find it: `nav.search.*`, comma-separated. */
  search?: TextKey;
  /** One of the places on the phone's bottom bar. */
  onBar?: boolean;
  /** The weakest role that sees it. Everyone, if absent. */
  minRole?: Role;
  /** Only tenants of these verticals see it. Everyone, if absent. */
  verticals?: ReadonlyArray<string>;
  /** Exact match for "active", for the root. */
  exact?: boolean;
  /** Only for someone with a role over an agency. */
  agencyOnly?: boolean;
}

const OUTREACH_VERTICALS = ["horeca", "auto", "agency-b2b"];

export const NAV: ReadonlyArray<NavItem> = [
  { id: "home", to: "/", label: "nav.home", icon: Sun, group: "today", search: "nav.search.home", onBar: true, exact: true },
  { id: "queue", to: "/queue", label: "nav.queue", icon: CheckSquare, group: "today", search: "nav.search.queue", onBar: true },
  { id: "calendar", to: "/calendar", label: "nav.calendar", icon: CalendarRange, group: "today", search: "nav.search.calendar", onBar: true },

  { id: "studio", to: "/studio", label: "nav.studio", icon: PenLine, group: "content", search: "nav.search.studio" },
  { id: "week-plan", to: "/week-plan", label: "nav.weekPlan", icon: CalendarCheck, group: "content", search: "nav.search.weekPlan" },
  { id: "themes", to: "/themes", label: "nav.themes", icon: Wand2, group: "content", search: "nav.search.themes" },
  { id: "topics", to: "/topics", label: "nav.topics", icon: Lightbulb, group: "content", search: "nav.search.topics" },
  { id: "post-kinds", to: "/post-kinds", label: "nav.postKinds", icon: Shapes, group: "content", search: "nav.search.postKinds" },
  { id: "post-templates", to: "/post-templates", label: "nav.postTemplates", icon: LayoutTemplate, group: "content", search: "nav.search.postTemplates" },
  { id: "competitors", to: "/competitors", label: "nav.competitors", icon: Binoculars, group: "content", search: "nav.search.competitors" },
  { id: "news", to: "/news", label: "nav.news", icon: Newspaper, group: "content", search: "nav.search.news" },
  { id: "calendar-events", to: "/calendar-events", label: "nav.calendarEvents", icon: CalendarDays, group: "content", search: "nav.search.calendarEvents" },
  { id: "library", to: "/library", label: "nav.library", icon: ImageIcon, group: "content", search: "nav.search.library" },
  { id: "visual-editor", to: "/visual-editor", label: "nav.visualEditor", icon: Shapes, group: "content", search: "nav.search.visualEditor" },

  { id: "ads", to: "/ads", label: "nav.ads", icon: Megaphone, group: "ads", search: "nav.search.ads" },
  { id: "tracking", to: "/tracking", label: "nav.tracking", icon: Activity, group: "ads", search: "nav.search.tracking" },
  { id: "forms", to: "/forms", label: "nav.forms", icon: FileText, group: "ads", search: "nav.search.forms" },
  { id: "links", to: "/links", label: "nav.links", icon: Link2, group: "ads", search: "nav.search.links" },

  { id: "leads", to: "/leads", label: "nav.leads", icon: ContactRound, group: "customers", search: "nav.search.leads", onBar: true },
  { id: "lead-sequences", to: "/lead-sequences", label: "nav.leadSequences", icon: ListOrdered, group: "customers", search: "nav.search.leadSequences" },
  { id: "customers", to: "/customers", label: "nav.customers", icon: UsersRound, group: "customers", search: "nav.search.customers" },
  { id: "segments", to: "/segments", label: "nav.segments", icon: Group, group: "customers", search: "nav.search.segments", verticals: OUTREACH_VERTICALS },
  { id: "proposals", to: "/proposals", label: "nav.proposals", icon: Sparkles, group: "customers", search: "nav.search.proposals", verticals: OUTREACH_VERTICALS },
  { id: "campaigns", to: "/campaigns", label: "nav.campaigns", icon: Send, group: "customers", search: "nav.search.campaigns", verticals: OUTREACH_VERTICALS },
  { id: "journeys", to: "/journeys", label: "nav.journeys", icon: Route, group: "customers", search: "nav.search.journeys" },
  { id: "templates", to: "/templates", label: "nav.templates", icon: MessageSquareText, group: "customers", search: "nav.search.templates" },
  { id: "referral", to: "/referral", label: "nav.referral", icon: Gift, group: "customers", search: "nav.search.referral" },
  { id: "consent", to: "/consent", label: "nav.consent", icon: ShieldCheck, group: "customers", search: "nav.search.consent", minRole: "editor" },

  { id: "analytics", to: "/analytics", label: "nav.analytics", icon: BarChart3, group: "results", search: "nav.search.analytics" },
  { id: "reports", to: "/reports", label: "nav.reports", icon: FileBarChart, group: "results", search: "nav.search.reports" },

  { id: "channels", to: "/channels", label: "nav.channels", icon: Radio, group: "setup", search: "nav.search.channels" },
  { id: "facts", to: "/facts", label: "nav.facts", icon: BookCheck, group: "setup", search: "nav.search.facts" },
  { id: "angles", to: "/angles", label: "nav.angles", icon: Target, group: "setup", search: "nav.search.angles" },
  { id: "brand", to: "/brand", label: "nav.brand", icon: Palette, group: "setup", search: "nav.search.brand" },
  { id: "knowledge", to: "/knowledge", label: "nav.knowledge", icon: Database, group: "setup", search: "nav.search.knowledge" },
  { id: "data-sources", to: "/data-sources", label: "nav.dataSources", icon: HardDriveDownload, group: "setup", search: "nav.search.dataSources" },
  { id: "messaging-settings", to: "/messaging-settings", label: "nav.messagingSettings", icon: SlidersHorizontal, group: "setup", search: "nav.search.messagingSettings", minRole: "editor" },
  { id: "billing", to: "/billing", label: "nav.billing", icon: CreditCard, group: "setup", search: "nav.search.billing", minRole: "owner" },
  { id: "ai-usage", to: "/ai-usage", label: "nav.aiUsage", icon: Gauge, group: "setup", search: "nav.search.aiUsage", minRole: "editor" },
  { id: "agency", to: "/agency", label: "nav.agency", icon: Building2, group: "setup", search: "nav.search.agency", agencyOnly: true },
  { id: "team", to: "/settings/team", label: "nav.team", icon: UsersRound, group: "setup", search: "nav.search.team" },
  { id: "notifications", to: "/settings/notifications", label: "nav.notifications", icon: BellRing, group: "setup", search: "nav.search.notifications" },
  { id: "settings", to: "/settings", label: "nav.settings", icon: Settings, group: "setup", search: "nav.search.settings" },
];

export const MAX_BOTTOM_BAR_ITEMS = 5;

const rank = (role: Role) => ROLES.length - ROLES.indexOf(role);
export const roleAtLeast = (role: Role, required: Role) => rank(role) >= rank(required);

export function visibleNav(items: ReadonlyArray<NavItem>, who: { role: Role; vertical: string; agency?: boolean }): NavItem[] {
  return items.filter((item) => (!item.agencyOnly || who.agency === true) && (!item.minRole || roleAtLeast(who.role, item.minRole)) && (!item.verticals || item.verticals.includes(who.vertical)));
}

/** At most five places on the bar: all of them when they fit, otherwise four and a More sheet with the rest. */
export function splitForBottomBar<T extends { onBar?: boolean }>(items: ReadonlyArray<T>, max = MAX_BOTTOM_BAR_ITEMS): { bar: T[]; more: T[] } {
  if (items.length <= max) return { bar: [...items], more: [] };
  // When a role or a vertical hides a chosen place, the next entry takes it, so the bar never looks broken.
  const chosen = new Set([...items.filter((item) => item.onBar), ...items.filter((item) => !item.onBar)].slice(0, max - 1));
  return { bar: items.filter((item) => chosen.has(item)), more: items.filter((item) => !chosen.has(item)) };
}

export function isActivePath(item: Pick<NavItem, "to" | "exact">, pathname: string): boolean {
  if (item.exact) return pathname === item.to;
  if (pathname !== item.to && !pathname.startsWith(`${item.to}/`)) return false;
  // A page with an entry of its own lights that entry, not the one above it as well.
  return !NAV.some((other) => other.to.startsWith(`${item.to}/`) && (pathname === other.to || pathname.startsWith(`${other.to}/`)));
}

export function groupOfPath(items: ReadonlyArray<NavItem>, pathname: string): NavGroupId | undefined {
  return items.find((item) => isActivePath(item, pathname))?.group;
}

/** The groups that still have something to show, in their order, with their entries in the given order. */
export function groupNav(items: ReadonlyArray<NavItem>): Array<{ group: NavGroup; items: NavItem[] }> {
  return NAV_GROUPS.flatMap((group) => {
    const inGroup = items.filter((item) => item.group === group.id);
    return inGroup.length > 0 ? [{ group, items: inGroup }] : [];
  });
}

// People type without diacritics, on a phone especially: "setari" has to find "Setări".
const foldForSearch = (text: string) => text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/** Every word typed has to appear in the entry's label or in its other words, in the person's language. */
export function searchNav(items: ReadonlyArray<NavItem>, query: string, text: (key: TextKey) => string): NavItem[] {
  const words = foldForSearch(query).split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    const haystack = foldForSearch(`${text(item.label)} ${item.search ? text(item.search) : ""}`);
    return words.every((word) => haystack.includes(word));
  });
}

export type GroupChoices = Partial<Record<NavGroupId, boolean>>;

/** What a browser remembers can be anything, from an older version or from a person's hand. */
export function parseGroupChoices(stored: string | null): GroupChoices {
  try {
    const parsed: unknown = JSON.parse(stored ?? "{}");
    if (typeof parsed !== "object" || parsed === null) return {};
    const choices: GroupChoices = {};
    for (const { id } of NAV_GROUPS) {
      const value = (parsed as Record<string, unknown>)[id];
      if (typeof value === "boolean") choices[id] = value;
    }
    return choices;
  } catch {
    return {};
  }
}
